/**
 * StockHomologationsPage - Gestão Rastreador › Homologação (listagem de todas as homologações).
 *
 * RELEASE: 2026-10-11 v1.0.0 - Versão inicial: KPIs, busca, filtros rápidos, ordenação e paginação.
 */
import { useEffect, useState } from 'react';
import { BadgeCheck, CheckCircle2, Plus, RefreshCw, Repeat, Search, XCircle } from 'lucide-react';
import { QUICK_FILTER_PILL } from '@/components/layout/list-page/quickFilterStyles';
import {
  ListPageLayout, ListPageHeader, ListPageKpis, KpiCard, ListPageFiltersBar, ListPageContent, SortableHeader,
  LIST_FILTERS_INPUT_CLASS, LIST_FILTERS_SELECT_TRIGGER_CLASS,
} from '@/components/layout/list-page';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { PaginationFooter } from '@/components/ui/pagination-footer';
import { usePermissionsContext } from '@/contexts/PermissionsContext';
import { useStockHomologationsList, type HomologationFilters } from '@/hooks/useStockHomologations';
import { HOMOLOGATION_RULES, HOMOLOGATION_TYPES } from '@/lib/stock-homologation';
import { StockHomologationDialog } from '@/components/stock/StockHomologationDialog';
import { StockHomologationDetailsDialog } from '@/components/stock/StockHomologationDetailsDialog';
import { StockHomologationsTable } from '@/components/stock/StockHomologationsTable';
import type { HomologationRow } from '@/hooks/useStockHomologations';
import { cn } from '@/lib/utils';

export default function StockHomologationsPage() {
  const { canCreate, isAdmin } = usePermissionsContext();
  const [searchInput, setSearchInput] = useState('');
  const [open, setOpen] = useState(false);
  // Homologação selecionada para exibição dos detalhes (somente leitura)
  const [selected, setSelected] = useState<HomologationRow | null>(null);
  const [filters, setFilters] = useState<HomologationFilters>({
    search: '', type: 'all', result: 'all', dateFrom: null, dateTo: null,
    sortField: 'created_at', sortDirection: 'desc', page: 1, pageSize: 25,
  });
  const { data, isLoading, isFetching, refetch } = useStockHomologationsList(filters);

  // Busca com espera de 350ms
  useEffect(() => {
    const t = setTimeout(() => setFilters((f) => (f.search === searchInput.trim() ? f : { ...f, search: searchInput.trim(), page: 1 })), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const onSort = (field: HomologationFilters['sortField']) => setFilters((f) => ({
    ...f, sortField: field, sortDirection: f.sortField === field && f.sortDirection === 'desc' ? 'asc' : 'desc', page: 1,
  }));
  const kpis = data?.kpis ?? {};
  const allowCreate = isAdmin || canCreate('stock_homologations');

  return (
    <ListPageLayout>
      <ListPageHeader
        title="Homologação"
        center={
          <div className="relative w-full max-w-md mx-4 hidden md:block">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground/60" />
            <Input placeholder="Buscar IMEI, ESN ou nº..." value={searchInput} maxLength={60}
              onChange={(e) => setSearchInput(e.target.value)}
              className="h-10 pl-10 pr-4 text-sm rounded-xl border-none bg-muted/40 hover:bg-muted/60 focus-visible:bg-background focus-visible:ring-1 focus-visible:ring-primary/20 focus-visible:ring-offset-0" />
          </div>
        }
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => refetch()} disabled={isFetching} aria-label="Atualizar">
              <RefreshCw className={cn('h-4 w-4', isFetching && 'animate-spin')} />
            </Button>
            {allowCreate && (
              <Button size="xs" onClick={() => setOpen(true)}><Plus className="mr-1.5 h-4 w-4" /> Nova homologação</Button>
            )}
          </div>
        }
      />
      <ListPageKpis>
        <KpiCard color="blue" icon={BadgeCheck} label="Homologações" value={kpis.total ?? 0} />
        <KpiCard color="green" icon={CheckCircle2} label="Aprovadas" value={kpis.approved ?? 0} />
        <KpiCard color="red" icon={XCircle} label="Reprovadas" value={kpis.rejected ?? 0} />
        <KpiCard color="purple" icon={Repeat} label="Alteraram situação" value={kpis.status_changed ?? 0} />
      </ListPageKpis>
      <ListPageFiltersBar isFetching={isFetching}>
        <div className="relative min-w-0 flex-1 sm:max-w-xs md:hidden">
          <Search className="absolute left-2.5 top-1/2 h-3 w-3 -translate-y-1/2 text-muted-foreground" />
          <Input className={`${LIST_FILTERS_INPUT_CLASS} w-full pl-8`} value={searchInput} maxLength={60}
            placeholder="Buscar IMEI, ESN ou nº..." onChange={(e) => setSearchInput(e.target.value)} />
        </div>
        <Select value={filters.type} onValueChange={(v) => setFilters((f) => ({ ...f, type: v as HomologationFilters['type'], page: 1 }))}>
          <SelectTrigger className={`${LIST_FILTERS_SELECT_TRIGGER_CLASS} ${QUICK_FILTER_PILL} shrink-0`}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os tipos</SelectItem>
            {HOMOLOGATION_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.result} onValueChange={(v) => setFilters((f) => ({ ...f, result: v as HomologationFilters['result'], page: 1 }))}>
          <SelectTrigger className={`${LIST_FILTERS_SELECT_TRIGGER_CLASS} ${QUICK_FILTER_PILL} shrink-0`}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os resultados</SelectItem>
            {HOMOLOGATION_RULES.status_change.map((r) => <SelectItem key={r.result} value={r.result}>{r.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input type="date" aria-label="De" className={`${LIST_FILTERS_INPUT_CLASS} ${QUICK_FILTER_PILL} w-36 shrink-0`} value={filters.dateFrom ?? ''}
          onChange={(e) => setFilters((f) => ({ ...f, dateFrom: e.target.value || null, page: 1 }))} />
        <Input type="date" aria-label="Até" className={`${LIST_FILTERS_INPUT_CLASS} ${QUICK_FILTER_PILL} w-36 shrink-0`} value={filters.dateTo ?? ''}
          onChange={(e) => setFilters((f) => ({ ...f, dateTo: e.target.value || null, page: 1 }))} />
      </ListPageFiltersBar>
      <ListPageContent fullBleed>
        <div className="overflow-x-auto">
          <StockHomologationsTable items={data?.items ?? []} isLoading={isLoading} onRowClick={setSelected}
            header={(label, field) => field
              ? <SortableHeader label={label} field={field} currentField={filters.sortField} currentDirection={filters.sortDirection} onSort={onSort} />
              : label} />
        </div>
        <PaginationFooter totalItems={data?.total ?? 0} currentPage={filters.page} pageSize={filters.pageSize}
          onPageChange={(page) => setFilters((f) => ({ ...f, page }))}
          onPageSizeChange={(pageSize) => setFilters((f) => ({ ...f, pageSize, page: 1 }))}
          pageSizeOptions={[10, 25, 50, 100]} />
      </ListPageContent>
      <StockHomologationDialog open={open} onOpenChange={setOpen} />
      <StockHomologationDetailsDialog item={selected} open={!!selected} onOpenChange={(o) => !o && setSelected(null)} />
    </ListPageLayout>
  );
}
